import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from 'recharts';
import { Users, BookOpen, FileText, Truck, Calendar } from 'lucide-react';
import Layout from '../components/Layout';
import WelcomeModal from '../components/WelcomeModal';
import {
  fetchDashboardStats,
  fetchCurrentIssueData,
  fetchPublications,
  fetchMagazines,
} from '../store/slices/bookingSlice';
import { dismissWelcome } from '../store/slices/authSlice';

const COLORS = ['#3b82f6', '#ef4444', '#10b981', '#f59e0b', '#8b5cf6', '#f97316'];

const StatCard = ({ title, value, icon: Icon, color, change }) => (
  <div className="bg-white overflow-hidden shadow rounded-lg">
    <div className="p-5">
      <div className="flex items-center">
        <div className="flex-shrink-0">
          <Icon className={`h-6 w-6 ${color}`} />
        </div>
        <div className="ml-5 w-0 flex-1">
          <dl>
            <dt className="text-sm font-medium text-gray-500 truncate">{title}</dt>
            <dd className="text-lg font-medium text-gray-900">
              {typeof value === 'number' && title.includes('£') ? `£${value.toFixed(2)}` : value}
            </dd>
          </dl>
        </div>
      </div>
      {change !== undefined && change !== null && (
        <div className="mt-2">
          <div className="flex items-center text-sm">
            <span className={change >= 0 ? 'text-green-600' : 'text-red-600'}>
              {change >= 0 ? '+' : ''}
              {change}%
            </span>
            <span className="text-gray-500 ml-2">from last issues</span>
          </div>
        </div>
      )}
    </div>
  </div>
);

const BookingDashboard = () => {
  const dispatch = useDispatch();
  const { dashboardStats, currentIssueData, publications, magazines } = useSelector(
    state => state.booking
  );
  const { user } = useSelector(state => state.auth);

  const [selectedMagazine, setSelectedMagazine] = useState('');
  const [showWelcomeModal, setShowWelcomeModal] = useState(false);
  const [hasCheckedWelcome, setHasCheckedWelcome] = useState(false);

  useEffect(() => {
    dispatch(fetchDashboardStats());
    dispatch(fetchPublications());
    dispatch(fetchMagazines());
  }, [dispatch]);

  useEffect(() => {
    if (magazines.length > 0 && !selectedMagazine) {
      setSelectedMagazine(magazines[0]._id);
    }
  }, [magazines, selectedMagazine]);

  useEffect(() => {
    if (selectedMagazine) {
      dispatch(fetchCurrentIssueData(selectedMagazine));
    }
  }, [dispatch, selectedMagazine]);

  useEffect(() => {
    if (user && !user.welcomeModalDismissed && !hasCheckedWelcome) {
      setShowWelcomeModal(true);
      setHasCheckedWelcome(true);
    }
  }, [user, hasCheckedWelcome]);

  const handleCloseWelcomeModal = dontShowAgain => {
    setShowWelcomeModal(false);
    if (dontShowAgain) {
      dispatch(dismissWelcome());
    }
  };

  const formatCurrency = value => `£${value?.toFixed(2)}`;

  return (
    <Layout>
      <WelcomeModal isOpen={showWelcomeModal} onClose={handleCloseWelcomeModal} />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Booking Dashboard</h1>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <StatCard
            title="Total Customers"
            value={dashboardStats.totalCustomers || 0}
            icon={Users}
            color="text-blue-600"
            change={dashboardStats.customerChange}
          />
          <StatCard
            title="Active Magazines"
            value={dashboardStats.totalMagazines || 0}
            icon={BookOpen}
            color="text-green-600"
            change={dashboardStats.magazineChange}
          />
          <StatCard
            title="Active Customers"
            value={dashboardStats.totalBookings || 0}
            icon={FileText}
            color="text-purple-600"
            change={dashboardStats.bookingChange}
          />
          <StatCard
            title="Leaflet Deliveries"
            value={dashboardStats.totalLeafletDeliveries || 0}
            icon={Truck}
            color="text-orange-600"
            change={dashboardStats.leafletDeliveryChange}
          />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
          <div className="bg-white p-6 rounded-lg shadow">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900">Current Issue Breakdown</h2>
              {magazines.length > 0 && (
                <select
                  value={selectedMagazine}
                  onChange={e => setSelectedMagazine(e.target.value)}
                  className="block w-48 px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                >
                  {magazines.map(magazine => (
                    <option key={magazine._id} value={magazine._id}>
                      {magazine.name}
                    </option>
                  ))}
                </select>
              )}
            </div>

            {currentIssueData ? (
              <>
                <div className="mb-4 p-4 bg-gray-50 rounded-lg">
                  <h3 className="font-medium text-gray-900">{currentIssueData.magazine}</h3>
                  <p className="text-sm text-gray-600">
                    Issue: {currentIssueData.currentIssue?.name} • Total Pages:{' '}
                    {currentIssueData.totalPages} • Booked: {currentIssueData.totalBookedPages}
                  </p>
                  <p className="text-sm text-gray-600">
                    Available: {currentIssueData.unallocatedPages} pages (
                    {(
                      (currentIssueData.unallocatedPages / currentIssueData.totalPages) *
                      100
                    ).toFixed(1)}
                    %)
                  </p>
                </div>

                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={currentIssueData.breakdown}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        label={false}
                        outerRadius={80}
                        fill="#8884d8"
                        dataKey="pages"
                      >
                        {currentIssueData.breakdown.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip formatter={(value, name) => [`${value} pages`, name]} />
                    </PieChart>
                  </ResponsiveContainer>
                </div>

                <div className="mt-4">
                  <h4 className="text-sm font-medium text-gray-900 mb-2">Breakdown Summary</h4>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    {currentIssueData.breakdown.map((item, index) => (
                      <div key={item.contentType} className="flex items-center">
                        <div
                          className="w-3 h-3 rounded-full mr-2"
                          style={{
                            backgroundColor: COLORS[index % COLORS.length],
                          }}
                        />
                        <span className="text-gray-600">
                          {item.contentType}: {item.pages} pages ({item.percentage}%)
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </>
            ) : (
              <div className="flex items-center justify-center h-64 text-gray-500">
                <div className="text-center">
                  <Calendar className="h-8 w-8 mx-auto mb-2" />
                  <p>No current issue data available</p>
                </div>
              </div>
            )}
          </div>

          <div className="bg-white p-6 rounded-lg shadow">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">
              Publications Revenue - Current Issue
            </h2>
            {publications.length > 0 ? (
              <div className="h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={publications.map(pub => ({
                      ...pub,
                      magazineShortName:
                        pub.magazine.name.length > 15
                          ? pub.magazine.name.substring(0, 15)
                          : pub.magazine.name,
                      combinedValue: pub.totalValue + (pub.leafletValue || 0),
                    }))}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis
                      dataKey="magazineShortName"
                      angle={-45}
                      textAnchor="end"
                      height={80}
                      interval={0}
                      tick={{ fontSize: 12 }}
                    />
                    <YAxis tickFormatter={value => `£${value.toFixed(2)}`} width={80} />
                    <Tooltip
                      formatter={value => [formatCurrency(value), 'Revenue']}
                      labelFormatter={label => {
                        const pub = publications.find(p => {
                          const shortName =
                            p.magazine.name.length > 15
                              ? p.magazine.name.substring(0, 15)
                              : p.magazine.name;
                          return shortName === label;
                        });
                        return pub ? pub.magazine.name : label;
                      }}
                    />
                    <Bar dataKey="combinedValue" fill="#3b82f6" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <div className="flex items-center justify-center h-80 text-gray-500">
                <div className="text-center">
                  <BarChart className="h-8 w-8 mx-auto mb-2" />
                  <p>No publications data available</p>
                </div>
              </div>
            )}

            {publications.length > 0 && (
              <div className="mt-4 pt-4 border-t border-gray-200">
                <div className="bg-blue-50 rounded-lg p-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                        Total Revenue
                      </h3>
                      <p className="text-sm text-gray-600">All magazines, current issue</p>
                    </div>
                    <div className="text-right">
                      <div className="text-3xl font-bold text-blue-600">
                        {formatCurrency(
                          publications.reduce(
                            (total, pub) => total + pub.totalValue + (pub.leafletValue || 0),
                            0
                          )
                        )}
                      </div>
                      <div className="text-sm text-gray-500">
                        {publications.reduce(
                          (total, pub) => total + pub.totalBookings + (pub.leafletCount || 0),
                          0
                        )}{' '}
                        active bookings
                      </div>
                    </div>
                  </div>

                  {/* Breakdown by magazine */}
                  <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                    {publications
                      .filter(
                        pub => pub.totalValue > 0 || (pub.leafletValue && pub.leafletValue > 0)
                      )
                      .map(pub => (
                        <div key={pub.magazine.id} className="bg-white rounded-md p-2 shadow-sm">
                          <div className="flex justify-between items-center">
                            <span
                              className="text-sm font-medium text-gray-900 truncate"
                              title={pub.magazine.name}
                            >
                              {pub.magazine.name.length > 15
                                ? pub.magazine.name.substring(0, 12) + '...'
                                : pub.magazine.name}
                            </span>
                            <span className="text-sm font-semibold text-blue-600 ml-2">
                              {formatCurrency(pub.totalValue + (pub.leafletValue || 0))}
                            </span>
                          </div>
                          <div className="text-xs text-gray-500 mt-1">
                            {pub.totalBookings + (pub.leafletCount || 0)} booking
                            {pub.totalBookings + (pub.leafletCount || 0) !== 1 ? 's' : ''}
                            {pub.currentIssue && ` • ${pub.currentIssue}`}
                          </div>
                        </div>
                      ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Publications Summary Table */}
        <div className="bg-white shadow overflow-hidden sm:rounded-md mb-8">
          <div className="px-4 py-5 sm:px-6">
            <h3 className="text-lg leading-6 font-medium text-gray-900">Publications Summary</h3>
            <p className="mt-1 max-w-2xl text-sm text-gray-500">
              Revenue breakdown by magazine and content type
            </p>
          </div>
          <div className="border-t border-gray-200">
            {publications.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Magazine
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Total Bookings
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Total Revenue
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Content Types
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {publications?.map(pub => (
                      <tr key={pub.magazine.id}>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          {pub.magazine.name}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {pub.totalBookings + (pub.leafletCount || 0)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {formatCurrency(pub.totalValue + (pub.leafletValue || 0))}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-500">
                          <div className="space-y-1">
                            {pub.contentTypeBreakdown.map(content => (
                              <div key={content.contentType} className="flex justify-between">
                                <span>{content.contentType}:</span>
                                <span>
                                  {content.count} ({formatCurrency(content.value)})
                                </span>
                              </div>
                            ))}
                            {pub.leafletCount > 0 && (
                              <div className="flex justify-between">
                                <span>Leaflets:</span>
                                <span>
                                  {pub.leafletCount} ({formatCurrency(pub.leafletValue)})
                                </span>
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="p-6 text-center text-gray-500">
                <FileText className="h-8 w-8 mx-auto mb-2" />
                <p>No publications data available</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default BookingDashboard;
